import React, { useState, useEffect } from 'react';

const GuestDirectory = () => {
  const [selectedFilters, setSelectedFilters] = useState({
    industry: 'All',
    topicFocus: 'All',
    guestType: 'All',
    sortBy: 'A-Z'
  });

  const [selectedGuest, setSelectedGuest] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guestEpisodes, setGuestEpisodes] = useState([]);
  const [episodesLoading, setEpisodesLoading] = useState(false);

  useEffect(() => {
    fetchGuests();
  }, []);

  const fetchGuests = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/guests/public`);
      if (!response.ok) {
        throw new Error('Failed to fetch guests');
      }
      const data = await response.json();
      setGuests(data);
    } catch (error) {
      console.error('Error fetching guests:', error);
      setGuests([]); // Set empty array as fallback
    } finally {
      setLoading(false);
    }
  };

  const fetchGuestEpisodes = async (guestId) => {
    setEpisodesLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/guests/${guestId}/episodes`);
      if (!response.ok) {
        throw new Error('Failed to fetch guest episodes');
      }
      const episodes = await response.json();
      setGuestEpisodes(episodes);
    } catch (error) {
      console.error('Error fetching guest episodes:', error);
      setGuestEpisodes([]);
    } finally {
      setEpisodesLoading(false);
    }
  };

  const handleGuestClick = (guest) => {
    setSelectedGuest(guest);
    setGuestEpisodes([]); // Clear previous episodes
    fetchGuestEpisodes(guest.id);
  };

  const filterOptions = {
    industry: ['All', 'Tech', 'Finance', 'Healthcare', 'Education', 'Creative'],
    topicFocus: ['All', 'Growth', 'Failure', 'Mental Health', 'Innovation', 'Leadership'],
    guestType: ['All', 'Founder', 'Creator', 'Investor', 'Artist', 'Coach'],
    sortBy: ['A-Z', 'Most Episodes', 'Recently Featured']
  };

  const LinkedInIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
    </svg>
  );

  const InstagramIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  );

  const WebsiteIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-5-9-9s4-9 9-9" />
    </svg>
  );

  const PlayIcon = () => (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 5v14l11-7z"/>
    </svg>
  );

  const CloseIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  return (
    <div className="min-h-screen bg-midnight text-warm-white font-geist">
      {/* Section Header */}
      <div className="pt-32 pb-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-clash text-5xl lg:text-7xl font-bold mb-6 tracking-tight">
            Direktorij gostiju
          </h1>
          <p className="text-xl lg:text-2xl text-soft-gray max-w-3xl mx-auto leading-relaxed font-geist">
            Naša dvorana legendi — digitalni zid umova koji su „oslobodili okvir" svoje priče s nama. Brzo otkrivanje, duboko istraživanje.
          </p>
        </div>
      </div>

      {/* Guest Grid */}
      <div className="px-6 lg:px-12 pb-20">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-electric-yellow text-lg">Učitava goste...</div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto">
            {guests.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-soft-gray text-xl">Nema dostupnih gostiju u ovom trenutku.</div>
                <p className="text-soft-gray mt-4">Vratite se kasnije za nevjerovatne priče!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {guests.map((guest) => (
                  <div
                    key={guest.id}
                    className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl overflow-hidden hover:scale-[1.02] hover:border-electric-yellow/20 transition-all duration-500 group cursor-pointer"
                    onClick={() => handleGuestClick(guest)}
                  >
                    {/* Guest Photo */}
                    <div className="relative overflow-hidden">
                      <img
                        src={guest.image_url || guest.imageUrl}
                        alt={guest.name}
                        className="w-full h-80 object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-midnight/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      {/* Episode Count Badge */}
                      <div className="absolute top-4 right-4">
                      </div>

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <button className="bg-electric-yellow text-midnight px-6 py-3 rounded-xl font-bold hover:scale-110 transition-transform duration-300">
                          Pogledaj profil
                        </button>
                      </div>
                    </div>

                    {/* Guest Content */}
                    <div className="p-6">
                      <h3 className="font-clash text-2xl font-bold mb-2 group-hover:text-electric-yellow transition-colors duration-300">
                        {guest.name}
                      </h3>
                      <p className="text-soft-gray mb-4">{guest.title_role || guest.titleRole}</p>

                      {/* Quote Preview */}
                      <blockquote className="text-electric-yellow/70 italic text-sm line-clamp-2 mb-4">
                        "{guest.quote}"
                      </blockquote>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="px-6 lg:px-12 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-deep-teal/20 to-electric-yellow/10 backdrop-blur-sm border border-electric-yellow/20 rounded-3xl p-12">
            <h2 className="font-clash text-4xl lg:text-5xl font-bold mb-6">
              Znate nekoga ko bi trebalo da bude ovdje?
            </h2>
            <p className="text-xl text-soft-gray mb-8">
              Pomozite nam da otkrijemo sljedeću priču „bez okvira" koja zaslužuje da bude ispričana.
            </p>
            
            <a href="/submit" className="bg-electric-yellow text-midnight px-8 py-4 rounded-xl font-bold text-lg hover:bg-electric-yellow/90 hover:scale-105 transition-all duration-300 inline-block">
              Predloži gosta
            </a>
          </div>
        </div>
      </div>

      {/* Guest Profile Modal */}
      {selectedGuest && (
        <div className="fixed inset-0 bg-midnight/95 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-charcoal/90 backdrop-blur-md border border-slate-dark/50 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="grid lg:grid-cols-5 gap-0">
              {/* Left Side - Guest Photo & Info */}
              <div className="lg:col-span-2 p-8 border-r border-slate-dark/30">
                <button
                  onClick={() => setSelectedGuest(null)}
                  className="absolute top-6 right-6 text-soft-gray hover:text-electric-yellow transition-colors duration-300"
                >
                  <CloseIcon />
                </button>

                <img
                  src={selectedGuest.image_url || selectedGuest.imageUrl}
                  alt={selectedGuest.name}
                  className="w-full aspect-square object-cover rounded-2xl grayscale mb-6"
                />

                <h2 className="font-clash text-3xl font-bold mb-2">{selectedGuest.name}</h2>
                <p className="text-electric-yellow font-medium mb-4">{selectedGuest.title_role || selectedGuest.titleRole}</p>

                {/* Social Links */}
                <div className="flex justify-center gap-4 mb-6">
                  {(selectedGuest.linkedin_url || selectedGuest.linkedinUrl) && (
                    <a href={selectedGuest.linkedin_url || selectedGuest.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-soft-gray hover:text-electric-yellow transition-colors duration-300">
                      <LinkedInIcon />
                    </a>
                  )}
                  {(selectedGuest.instagram_url || selectedGuest.instagramUrl) && (
                    <a href={selectedGuest.instagram_url || selectedGuest.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-soft-gray hover:text-electric-yellow transition-colors duration-300">
                      <InstagramIcon />
                    </a>
                  )}
                  {(selectedGuest.website_url || selectedGuest.websiteUrl) && (
                    <a href={selectedGuest.website_url || selectedGuest.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-soft-gray hover:text-electric-yellow transition-colors duration-300">
                      <WebsiteIcon />
                    </a>
                  )}
                </div>
              </div>

              {/* Right Side - Content */}
              <div className="lg:col-span-3 p-8">
                {/* Quote */}
                <blockquote className="font-clash text-2xl lg:text-3xl leading-tight mb-8 text-electric-yellow">
                  "{selectedGuest.quote}"
                </blockquote>

                {/* Bio */}
                <div className="mb-8">
                  <h3 className="font-bold text-xl mb-4">O {selectedGuest.name.split(' ')[0]}</h3>
                  <p className="text-soft-gray leading-relaxed">{selectedGuest.bio}</p>
                </div>

                {/* Episodes */}
                <div>
                  <h3 className="font-bold text-xl mb-4">Epizode</h3>
                  <div className="space-y-4">
                    {episodesLoading ? (
                      <div className="text-center py-4">
                        <div className="text-electric-yellow">Učitava epizode...</div>
                      </div>
                    ) : guestEpisodes.length > 0 ? (
                      guestEpisodes.map((episode) => (
                        <div key={episode.id} className="bg-midnight/60 rounded-xl p-4 flex items-center justify-between">
                          <div className="flex items-center gap-4 text-left">
                            <span className="text-electric-yellow text-sm font-medium bg-deep-teal/20 px-3 py-1 rounded-full">
                              {episode.episode_number || episode.episodeNumber}
                            </span>
                            <div className="text-left">
                              <h4 className="font-bold text-left">{episode.title}</h4>
                              <p className="text-soft-gray text-sm">{episode.duration}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {(episode.youtube_url || episode.youtubeUrl) && (
                              <a
                                href={episode.youtube_url || episode.youtubeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-electric-yellow text-midnight p-3 rounded-full hover:scale-110 transition-transform duration-300"
                                title="Watch on YouTube"
                              >
                                <PlayIcon />
                              </a>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="bg-midnight/60 rounded-xl p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4 text-left">
                          <span className="text-electric-yellow text-sm font-medium bg-deep-teal/20 px-3 py-1 rounded-full">
                            USKORO
                          </span>
                          <div className="text-left">
                            <h4 className="font-bold text-left">Epizoda sa {selectedGuest.name.split(' ')[0]} stiže uskoro!</h4>
                          </div>
                        </div>
                        <button className="bg-electric-yellow text-midnight p-3 rounded-full hover:scale-110 transition-transform duration-300">
                          <PlayIcon />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestDirectory;